import { OrganizationSelectors } from '@copilot/common/store/selectors/organization';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { isEmpty, isNil } from 'lodash';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { canCreateCampaignTeam, canSeeTeamMembers } from '@copilot/common/utils/campaign';
import { CampaignFilterType } from '../campaignTypeDropdown';
import { FilterDefinition } from '@copilot/common/components/componentModels/filterDefinition';
import { useCampaignsWithOrgMemberQuery } from '@copilot/data/graphql/_generated';
import moment from 'moment';
import { ALL_CAMPAIGNS_LAST_NUM_DAYS } from '../allCampaignsPageConstants';
import { CampaignSummaryModel } from '../allCampaignsTable';
import { toCampaignSummaryModel } from '../allCampaignsPageHelpers';
import { CampaignManager } from '@copilot/data';
import Store from '@copilot/common/store/storeClass';
import { CampaignActions } from '@copilot/common/store/actions/campaign';
import notificationManager from '@copilot/common/utils/notificationManager';

/**
 * Hook for the All Campaigns page, handles the state and logic for fetching campaigns and associated org member data.
 */
export function useAllCampaignsPage() {
    const organizationId = useSelector(OrganizationSelectors.getActiveOrganizationId);
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    const thirtyDaysAgoMs = useMemo(() => {
        const thirtyDaysAgo = moment().subtract(ALL_CAMPAIGNS_LAST_NUM_DAYS, 'days');
        const midnight = thirtyDaysAgo.startOf('day');
        return midnight.valueOf();
    }, []);

    const {
        data,
        error: campaignSummariesError,
        loading,
        refetch,
    } = useCampaignsWithOrgMemberQuery({
        variables: {
            start: thirtyDaysAgoMs,
        },
    });

    const campaignSummaryData = useMemo(() => {
        if (isEmpty(data) || isEmpty(data?.campaignSummaries)) {
            return [];
        } else {
            return data?.campaignSummaries.map<CampaignSummaryModel>(toCampaignSummaryModel) ?? [];
        }
    }, [data]);

    const [canEdit, setCanEdit] = useState(false);
    const [showTeamMembers, setShowTeamMembers] = useState(false);
    useEffect(() => {
        setCanEdit(canCreateCampaignTeam(activeMember));
        setShowTeamMembers(canSeeTeamMembers(activeMember));
    }, [activeMember]);

    const [searchTerm, setSearchTerm] = useState('');
    const [campaignFilterType, setCampaignFilterType] = useState<CampaignFilterType>(
        CampaignFilterType.All
    );
    const [teamMemberFilters, setTeamMemberFilters] = useState<FilterDefinition[]>([]);
    useEffect(() => {
        const orgMembers = Array.from(
            new Map(
                (data?.campaignSummaries ?? []).map((m) => [m.orgMember.id, m.orgMember])
            ).values()
        );
        const isMemberIdFilteredMap: { [key: string]: boolean } = {};
        for (const filter of teamMemberFilters) {
            isMemberIdFilteredMap[filter.key] = filter.isVisible;
        }

        // only update team member filters when there is difference
        if (teamMemberFilters.length === orgMembers.length) {
            const hasNewMember = orgMembers.some((member) => {
                return isNil(isMemberIdFilteredMap[member.id]);
            });
            if (!hasNewMember) return;
        }

        // create team member filters based on the list of org members
        setTeamMemberFilters(
            orgMembers.map((m) => {
                const memberId = m.id;
                return new FilterDefinition({
                    key: memberId,
                    label: m.userInfo.fullName,
                    isVisible: isMemberIdFilteredMap[memberId] ?? false,
                });
            })
        );
    }, [data]);

    /**
     * Handles the deletion of a campaign. Dispatches the delete action to redux (to support sidebar updates)
     * and triggers the refetch of campaigns.
     * @param campaignId
     */
    async function onDeleteCampaign(campaignId: string) {
        try {
            await CampaignManager.deleteCampaign(campaignId);
            Store.Dispatch(CampaignActions.deleteCampaign({ id: campaignId }));
            notificationManager.showSuccessNotification({
                message: 'Campaign Deleted',
                description: 'Campaign has been deleted.',
            });
            await refetch();
        } catch (error) {
            notificationManager.showErrorNotification({
                message: 'Failed',
                description: 'Please refresh the page and try again',
            });
        }
    }

    return {
        organizationId,
        activeMember,
        isLoading: isEmpty(organizationId) || isEmpty(activeMember) || loading,
        campaignSummaryData,
        canEditCampaigns: canEdit,
        showTeamMembers,
        searchTerm,
        campaignFilterType,
        teamMemberFilters,
        onCampaignFilterChanged: setCampaignFilterType,
        onTeamMembersFilterChanged: setTeamMemberFilters,
        onSearchTermChanged: setSearchTerm,
        onDeleteCampaign,
        isError: !isNil(campaignSummariesError),
    };
}
